import type {
  Collaborator,
  CollaboratorProjection,
  CollaboratorContract,
  CollaboratorUnavailabilityCalendarItem,
  HrAnalyticsResponse,
  InterimAgency,
  HrDashboardCoverageResponse,
  HrDashboardOverviewResponse,
  HrDashboardPlanningStatusResponse,
  HrDashboardTrendsResponse,
  HrEffectifDetailResponse,
  HrFraisDetailResponse,
  HrRemoteStorePeriodMetricsResponse,
  HrSalesPeaksResponse,
  HrTheftPeaksResponse,
  HrStoreWeeklyMetricsResponse,
  HrSuiviParBoutiqueResponse,
  FetchTransportSegmentsParams,
  NetworkDashboardAvailableStore,
  PlanningConfig,
  PlanningLastOptimization,
  TransportSegmentDisplay,
  WorkShiftProjection,
} from '@gozoki/api-types'
import { gozokiApiWithAuth } from '../gozokiApi'

export type { FetchTransportSegmentsParams } from '@gozoki/api-types'

// Fetch one collaborator by id
export const fetchCollaborator = async (id: number) => {
  const response = await gozokiApiWithAuth.get<CollaboratorProjection>(`/hr/collaborators/${id}`)
  return response.data
}

// Fetch the contract history for a collaborator (most recent first)
export const fetchCollaboratorContracts = async (collaboratorId: number) => {
  const response = await gozokiApiWithAuth.get<CollaboratorContract[]>(
    `/hr/collaborators/${collaboratorId}/contracts`,
  )
  return response.data
}

// Fetch interim agencies (referential for interim contracts)
export const fetchInterimAgencies = async (includeArchived?: boolean) => {
  const response = await gozokiApiWithAuth.get<InterimAgency[]>('/hr/interim-agencies', {
    params: includeArchived ? { includeArchived: 'true' } : undefined,
  })
  return response.data
}

export interface FetchWorkShiftsParams {
  storeId?: number
  /** Affichage pluri-boutique (flag `rh-pointage-display-pluri-boutique`) : sérialisé en CSV. */
  storeIds?: number[]
  collaboratorId?: number
  date?: string
  dateStart?: string
  dateEnd?: string
  clockedOnly?: boolean
  includeClockValues?: boolean
  /**
   * Vue « pointage » par jour : applique le rattachement intérim « journée de début » (un créneau
   * du petit matin de J+1 suivant une nuit avec coupure < 2h est renvoyé sur J, et exclu de J+1).
   * Uniquement pris en compte avec `date`.
   */
  attachInterimNightShifts?: boolean
}

export interface FetchAvailableCollaboratorsParams {
  storeId: number
  date: string
  startTime: string
  endTime: string
  excludeWorkShiftId?: number
}

export interface FetchManualAttendanceCandidatesParams {
  storeId: number
  date: string
}

export interface FetchHrAnalyticsParams {
  storeId?: number
  dateStart?: string
  dateEnd?: string
}

export interface FetchHrSalesPeaksParams {
  storeId: number
}

export interface FetchHrTheftPeaksParams {
  storeId: number
}

export interface FetchHrStoreWeeklyMetricsParams {
  storeId: number
  dateStart: string
  dateEnd: string
}

export interface FetchHrRemoteStorePeriodMetricsParams {
  storeId: number
  dateStart: string
  dateEnd: string
}

export interface FetchHrUnavailabilityCalendarParams {
  dateStart: string
  dateEnd: string
  storeId?: number
}

export interface FetchHrDashboardScopeParams {
  // Optional store filter; omitted = whole network.
  storeId?: number
  // Multi-sélection (onglet Tableau de bord) — prioritaire sur storeId côté backend.
  storeIds?: number[]
}

export interface FetchHrDashboardParams extends FetchHrDashboardScopeParams {
  dateStart: string
  dateEnd: string
}

export interface FetchHrDashboardTrendsParams extends FetchHrDashboardScopeParams {
  dateEnd: string
}

export interface FetchHrDashboardPlanningStatusParams extends FetchHrDashboardScopeParams {
  /** Front-only cache boundary. The endpoint always evaluates the current S+1 week. */
  referenceWeekStart: string
}

// Fetch all work shifts
export const fetchWorkShifts = async (params?: FetchWorkShiftsParams) => {
  const { storeIds, ...rest } = params ?? {}
  // `storeIds` sérialisé en CSV (`storeIds=1,2`) : axios enverrait sinon `storeIds[]=` que le
  // parseur CSV côté vine n'éclate pas. Même convention que la mutation export.
  const queryParams: Record<string, unknown> = { ...rest }
  if (storeIds && storeIds.length > 0) {
    queryParams.storeIds = storeIds.join(',')
  }
  const response = await gozokiApiWithAuth.get<WorkShiftProjection[]>('/hr/work-shifts', {
    params: queryParams,
  })
  return response.data
}

// Fetch transport (déplacement payé) display blocks for a store's planning
export const fetchTransportSegments = async (params: FetchTransportSegmentsParams) => {
  const response = await gozokiApiWithAuth.get<TransportSegmentDisplay[]>(
    '/hr/planning/transport-segments',
    { params },
  )
  return response.data
}

// Fetch collaborators available for a shift window
export const fetchAvailableCollaborators = async (params: FetchAvailableCollaboratorsParams) => {
  const response = await gozokiApiWithAuth.get<Collaborator[]>('/hr/collaborator/available', {
    params,
  })
  return response.data
}

export const fetchManualAttendanceCandidates = async ({
  storeId,
  date,
}: FetchManualAttendanceCandidatesParams) => {
  const response = await gozokiApiWithAuth.get<CollaboratorProjection[]>('/hr/collaborators', {
    params: { storeId, actionDate: date },
  })
  return response.data
}

export const fetchHrAnalytics = async (params?: FetchHrAnalyticsParams) => {
  const response = await gozokiApiWithAuth.get<HrAnalyticsResponse>('/hr/analytics', {
    params,
  })
  return response.data
}

export const fetchHrSalesPeaks = async (params?: FetchHrSalesPeaksParams) => {
  const response = await gozokiApiWithAuth.get<HrSalesPeaksResponse>('/hr/analytics/sales-peaks', {
    params,
  })
  return response.data
}

export const fetchHrTheftPeaks = async (params?: FetchHrTheftPeaksParams) => {
  const response = await gozokiApiWithAuth.get<HrTheftPeaksResponse>('/hr/analytics/theft-peaks', {
    params,
  })
  return response.data
}

export const fetchHrStoreWeeklyMetrics = async ({
  storeId,
  dateStart,
  dateEnd,
}: FetchHrStoreWeeklyMetricsParams) => {
  const response = await gozokiApiWithAuth.get<HrStoreWeeklyMetricsResponse>(
    `/hr/store-metrics/${storeId}/period`,
    {
      params: {
        dateStart,
        dateEnd,
      },
    },
  )
  return response.data
}

export const fetchHrRemoteStorePeriodMetrics = async ({
  storeId,
  dateStart,
  dateEnd,
}: FetchHrRemoteStorePeriodMetricsParams) => {
  const response = await gozokiApiWithAuth.get<HrRemoteStorePeriodMetricsResponse>(
    `/hr/remote-store-metrics/${storeId}/period`,
    {
      params: {
        dateStart,
        dateEnd,
      },
    },
  )
  return response.data
}

export const fetchHrDashboardOverview = async (
  params: FetchHrDashboardParams,
  signal?: AbortSignal,
) => {
  const response = await gozokiApiWithAuth.get<HrDashboardOverviewResponse>(
    '/hr/dashboard/overview',
    { params, signal },
  )
  return response.data
}

export const fetchHrDashboardTrends = async (
  params: FetchHrDashboardTrendsParams,
  signal?: AbortSignal,
) => {
  const response = await gozokiApiWithAuth.get<HrDashboardTrendsResponse>('/hr/dashboard/trends', {
    params,
    signal,
  })
  return response.data
}

export const fetchHrDashboardCoverage = async (
  params: FetchHrDashboardParams,
  signal?: AbortSignal,
) => {
  const response = await gozokiApiWithAuth.get<HrDashboardCoverageResponse>(
    '/hr/dashboard/coverage',
    { params, signal },
  )
  return response.data
}

export const fetchHrDashboardPlanningStatus = async (
  params: FetchHrDashboardPlanningStatusParams,
  signal?: AbortSignal,
) => {
  const { referenceWeekStart: _referenceWeekStart, ...requestParams } = params
  const response = await gozokiApiWithAuth.get<HrDashboardPlanningStatusResponse>(
    '/hr/dashboard/plannings-status',
    { params: requestParams, signal },
  )
  return response.data
}

export const fetchHrEffectifDetail = async (
  params: FetchHrDashboardParams,
  signal?: AbortSignal,
) => {
  const response = await gozokiApiWithAuth.get<HrEffectifDetailResponse>(
    '/hr/dashboard/effectif-detail',
    { params, signal },
  )
  return response.data
}

export const fetchHrFraisDetail = async (params: FetchHrDashboardParams, signal?: AbortSignal) => {
  const response = await gozokiApiWithAuth.get<HrFraisDetailResponse>(
    '/hr/dashboard/frais-detail',
    {
      params,
      signal,
    },
  )
  return response.data
}

export const fetchHrSuiviParBoutique = async (
  params: FetchHrDashboardParams,
  signal?: AbortSignal,
) => {
  const response = await gozokiApiWithAuth.get<HrSuiviParBoutiqueResponse>(
    '/hr/dashboard/suivi-par-boutique',
    { params, signal },
  )
  return response.data
}

export const fetchHrDashboardAvailableStores = async (signal?: AbortSignal) => {
  const response = await gozokiApiWithAuth.get<NetworkDashboardAvailableStore[]>(
    '/hr/dashboard/available-stores',
    { signal },
  )
  return response.data
}

export const fetchHrUnavailabilityCalendar = async (
  params: FetchHrUnavailabilityCalendarParams,
) => {
  const response = await gozokiApiWithAuth.get<CollaboratorUnavailabilityCalendarItem[]>(
    '/hr/collaborators/unavailabilities/calendar',
    {
      params,
    },
  )
  return response.data
}

// Fetch planning configuration (global + per-store)
export const fetchPlanningConfig = async () => {
  const response = await gozokiApiWithAuth.get<PlanningConfig>('/hr/planning/config')
  return response.data
}

export interface FetchLastOptimizationParams {
  dateStart: string
  dateEnd: string
}

export const fetchLastOptimization = async (params: FetchLastOptimizationParams) => {
  const response = await gozokiApiWithAuth.get<{
    lastOptimization: PlanningLastOptimization | null
  }>('/hr/planning/last-optimization', { params })
  return response.data.lastOptimization
}
